import * as Sentry from '@sentry/browser'
import { fanoutLogger, sentryWrapper } from '../api/helpers/logger'
import { localStorageGet } from './utils'

import type { StandardizedLogger } from '../api/helpers/logger/types'

export type SentryLogStep =
  | 'connect_start'
  | 'savvy_flow_read'
  | 'load_user_data_before'
  | 'load_user_data_after'
  | 'connect_complete'
  | 'page_connect'
  | 'split_persist_check'
  | 'eval_connect_failed'
  | 'eval_user_data_written'

export type SentryTrafficTag = 'preview' | 'production'

//* Shape of logs deferred by renderer_eval_js_v3 before the engine bundle boots Sentry.
//* Pushed to window.__EMBEDDABLES_EVAL_ERRORS__ and flushed once Sentry is up (boot/load),
//* or immediately when the eval itself sees __EMBEDDABLES_FLUSH_EVAL_ERRORS__ already set.
export type DeferredEvalError = {
  scope: string
  step: SentryLogStep
  flow_id?: string
  message?: string
  stack?: string
  timestamp?: number
  props?: Record<string, unknown>
}

declare global {
  interface Window {
    __EMBEDDABLES_EVAL_ERRORS__?: DeferredEvalError[]
    //* Exposed by the bundle so renderer_eval_js_v3 can drain the queue as soon as the eval
    //* finishes when the bundle has already booted (SSR race — eval after bundle).
    __EMBEDDABLES_FLUSH_EVAL_ERRORS__?: (options?: FlushDeferredEvalErrorsOptions) => void
    //* Set by renderer_eval_js_v3 after a successful SavvyFormUserData write, keyed by
    //* flow id so multi-embed pages do not overwrite each other's eval_to_connect_ms.
    __EMBEDDABLES_EVAL_WRITE_AT__?: Record<string, number>
  }
}

export type FlushDeferredEvalErrorsOptions = {
  //* Which call site drained the queue — bundle_boot | window_load | eval_v3.
  flush_source?: string
}

const SENTRY_STEP_MESSAGES: Record<SentryLogStep, string> = {
  connect_start: 'Connect started',
  savvy_flow_read: 'SavvyFlow read',
  load_user_data_before: 'Loading user data before connect',
  load_user_data_after: 'User data loaded after connect',
  connect_complete: 'Connect completed',
  page_connect: 'Page connected',
  split_persist_check: 'Split keys compared before first user data persist',
  eval_connect_failed: 'Eval v3 threw before bundle boot',
  eval_user_data_written: 'Eval v3 wrote user data to local storage',
}

export type LogEntry = {
  scope: string
  functionName: string
  step: SentryLogStep
  props?: Record<string, unknown>
}

const bootstrapLogger: StandardizedLogger = fanoutLogger({
  loggers: [sentryWrapper(Sentry.logger)],
})

//* Classify browser Sentry traffic for observability.
//* - preview: CLI dev (localhost) or engine hosted preview (/preview/…)
//* - production: customer-site embeds and other live traffic
export function getSentryTrafficTag({
  hostname,
  pathname,
}: Pick<Location, 'hostname' | 'pathname'>): SentryTrafficTag {
  if (hostname.startsWith('localhost') || hostname === '127.0.0.1') {
    return 'preview'
  }

  if (pathname === '/preview' || pathname.startsWith('/preview/')) {
    return 'preview'
  }

  if (pathname.startsWith('/builder-preview/')) {
    return 'preview'
  }

  return 'production'
}

export function countSplitKeys({
  data,
}: {
  data: Record<string, unknown> | undefined | null
}): number {
  if (!data) return 0
  return Object.keys(data).filter((key) => key.startsWith('split_')).length
}

export function getSplitKeyCountFromLocalStorage({ flowId }: { flowId: string }): number {
  try {
    const allData = JSON.parse(localStorageGet('SavvyFormUserData') || '{}') as Record<
      string,
      Record<string, unknown>
    >
    return countSplitKeys({ data: allData[flowId] })
  } catch {
    return 0
  }
}

export type SavvyFormUserDataReadResult =
  | 'ok'
  | 'null'
  | 'empty'
  | 'missing_flow'
  | 'parse_error'
  | 'read_error'

export type SavvyFormUserDataReadInspection = {
  local_storage_split_key_count: number
  storage_read_ok: boolean
  storage_read_error: string | null
  storage_read_result: SavvyFormUserDataReadResult
}

function formatStorageError(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`
  return String(error)
}

//* Diagnostic read of SavvyFormUserData for load_user_data_before. Uses raw localStorage.getItem
//* so getItem failures are visible — localStorageGet swallows them and returns undefined.
export function inspectSavvyFormUserDataRead({
  flowId,
}: {
  flowId: string
}): SavvyFormUserDataReadInspection {
  let raw: string | null
  try {
    raw = localStorage.getItem('SavvyFormUserData')
  } catch (error) {
    return {
      local_storage_split_key_count: 0,
      storage_read_ok: false,
      storage_read_error: formatStorageError(error),
      storage_read_result: 'read_error',
    }
  }

  if (raw === null) {
    return {
      local_storage_split_key_count: 0,
      storage_read_ok: true,
      storage_read_error: null,
      storage_read_result: 'null',
    }
  }

  if (raw === '' || raw === '{}') {
    return {
      local_storage_split_key_count: 0,
      storage_read_ok: true,
      storage_read_error: null,
      storage_read_result: 'empty',
    }
  }

  try {
    const allData = JSON.parse(raw) as Record<string, Record<string, unknown>>
    const flowData = allData[flowId]
    if (!flowData) {
      return {
        local_storage_split_key_count: 0,
        storage_read_ok: true,
        storage_read_error: null,
        storage_read_result: 'missing_flow',
      }
    }

    return {
      local_storage_split_key_count: countSplitKeys({ data: flowData }),
      storage_read_ok: true,
      storage_read_error: null,
      storage_read_result: 'ok',
    }
  } catch (error) {
    return {
      local_storage_split_key_count: 0,
      storage_read_ok: false,
      storage_read_error: formatStorageError(error),
      storage_read_result: 'parse_error',
    }
  }
}

//* Emit a structured bootstrap log via the shared fanoutLogger + sentryWrapper.
export function logSentryStep({ scope, functionName, step, props }: LogEntry) {
  if (!Sentry.getClient()?.getDsn()) return

  try {
    bootstrapLogger.info({
      scope,
      functionName,
      message: SENTRY_STEP_MESSAGES[step],
      props: {
        step,
        traffic: getSentryTrafficTag({
          hostname: window.location.hostname,
          pathname: window.location.pathname,
        }),
        hostname: window.location.hostname,
        ...props,
      },
    })
  } catch {
    // Do not let logging failures impact embed rendering.
  }
}

//* Drain logs recorded by renderer_eval_js_v3 before Sentry was available and forward them
//* through the structured logging pipeline. Safe to call multiple times: the queue is
//* emptied as it is read.
export function flushDeferredEvalErrors({
  flush_source,
}: FlushDeferredEvalErrorsOptions = {}): void {
  if (!Sentry.getClient()?.getDsn()) return

  const queue = window.__EMBEDDABLES_EVAL_ERRORS__
  if (!Array.isArray(queue) || queue.length === 0) return

  //* Remove the entries synchronously so a concurrent flush cannot re-send them.
  const deferredErrors = queue.splice(0, queue.length)

  deferredErrors.forEach((deferredError) => {
    logSentryStep({
      scope: deferredError.scope,
      functionName: 'evalV3',
      step: deferredError.step,
      props: {
        flow_id: deferredError.flow_id,
        message: deferredError.message,
        stack: deferredError.stack,
        timestamp: deferredError.timestamp,
        flush_source,
        ...deferredError.props,
      },
    })
  })
}
